/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect, useRef } from 'react'
import { useServiceContext } from '../../../../root'
import { useModal } from '../../../../contexts/ModalContext'
import Modal from '../../../../components/Modal'
import Paginate from '../paginate/Paginate'
import type { EosPaginationMetadata } from '../../../../types/eos'

interface EosWorkflowInstructionsReviewProps {
  instructionsData: { uuid: string; [key: string]: any }
}

const STUDY_SITE = 'Study/Site'

const EosWorkflowInstructionsReview: React.FC<EosWorkflowInstructionsReviewProps> = ({ instructionsData }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const { showModal, hideModal } = useModal()

  const [savedInstructions, setSavedInstructions] = useState<any>(null)
  const [isOpen, setIsOpen] = useState(true)
  const [isModalShowable, setIsModalShowable] = useState(false)
  const [adminDetails, setAdminDetails] = useState<{ name?: string; email?: string }>({})
  const [rows, setRows] = useState<any[]>([])
  const [tableRows, setTableRows] = useState<any[]>([])

  const metadata = useRef<EosPaginationMetadata>({
    sortBy: 'name',
    sortDirection: 'asc',
    pageSize: context.service.PageSize!.get() >= 25 ? 25 : context.service.PageSize!.get(),
    currentPage: 1,
    totalItems: 0,
    totalUsersShown: 0,
    totalSitesFiltered: 0
  })

  useEffect(() => {
    context.api.eos.getSubmittedWorkflowinstruction(instructionsData).then(
      (data: any) => {
        const finalData = data?.plain ? data.plain() : data
        setSavedInstructions(finalData)
        setIsModalShowable(true)
      },
      (err: any) => {
        context.service.toast.error(err?.body?.message ?? err?.data?.message)
        doClose()
      }
    )
    const userData = context.service.auth.getUser()
    setAdminDetails({ name: userData?.fullName, email: userData?.email })
  }, [])

  useEffect(() => {
    if (savedInstructions) {
      const studyDTOData = JSON.parse(savedInstructions?.studyDTO)
      generateRows(studyDTOData)
    }
  }, [savedInstructions])

  useEffect(() => {
    calculateItems()
    createTableRows()
  }, [rows])

  const createTableRows = () => {
    const min = metadata.current.currentPage ? (metadata.current.currentPage - 1) * metadata.current.pageSize : 0
    const max = min + metadata.current.pageSize
    let counter = 0
    const tableRowsData: any[] = []

    rows.forEach((row) => {
      if (counter < max) {
        if (counter >= min) {
          tableRowsData.push(row)
          if (!row.isSite) counter++
        } else {
          if (row.isUser) counter++
        }
      }
    })
    setTableRows(tableRowsData)
  }

  const generateRows = (data: any) => {
    const rowsData: any[] = []
    ;(data.sites ?? []).forEach((site: any) => {
      rowsData.push({ isSite: true, name: site.name, rowId: site?.uuid })
      ;(site.grantAccessToUsers ?? []).forEach((user: any) => {
        rowsData.push({
          isUser: true,
          name: `${user?.firstName} ${user?.lastName}`,
          email: user?.email,
          isAccessSelected: user?.grantAccess,
          isDownloadSelected: user?.createDownloadTask,
          isAcknowledgementSelected: user?.createAcknowledgementTask,
          rowId: user?.uuid
        })
      })
      ;(site.removeAccessFromUsers ?? []).forEach((user: any) => {
        rowsData.push({
          isUser: true,
          name: `${user?.firstName} ${user?.lastName}`,
          email: user?.email,
          isAccessSelected: user?.grantAccess,
          isDownloadSelected: user?.createDownloadTask,
          isAcknowledgementSelected: user?.createAcknowledgementTask,
          rowId: user?.uuid
        })
      })
    })
    setRows(rowsData)
  }

  const changePage = (newValue: number) => {
    metadata.current.currentPage = newValue
    calculateItems()
    createTableRows()
  }

  const changeItemsPerPage = (newValue: number) => {
    metadata.current.pageSize = newValue
  }

  const calculateItems = () => {
    metadata.current = {
      ...metadata.current,
      totalItems: 0,
      totalUsersShown: rows.filter((r) => r.isUser).length,
      totalSitesFiltered: 0,
      pageSize: context.service.PageSize!.get() >= 25 ? 25 : context.service.PageSize!.get()
    }
  }

  const revertInstructions = () => {
    import('./InstructionsRevertCommentModal').then(({ default: InstructionsRevertCommentModal }) => {
      showModal(<InstructionsRevertCommentModal uuid={instructionsData?.uuid} closeModal={doClose} />, {
        keepParentModalsOpen: true
      })
    })
  }

  const startEos = () => {
    context.api.eos.processSubmittedWorkflowinstruction(instructionsData?.uuid).then(
      () => {
        context.service.toast.success(translate('EOS_SETUP_USERS_WORKFLOWS_SUCCESS'))
        context.$rootScope.$emit('updateWfInstructionsTable')
        doClose()
      },
      (err: any) => {
        context.service.toast.error(err?.body?.message ?? err?.data?.message)
        doClose()
      }
    )
  }

  const doClose = () => {
    setIsOpen(false)
    hideModal()
  }

  if (!isModalShowable) return null

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate('EOS_SETUP_USERS_WORKFLOWS_REVIEW_TITLE')}
      size="lg"
      className="setup-users-and-wf-container step-3"
    >
      <Modal.Body>
        <div className="setup-users-and-wf-container review-process">
          <div className="fs12">
            <label>{translate('EOS_SETUP_USERS_WORKFLOWS_REVIEW_PROCESS_LABEL')}</label>
            <div className="spacer">{translate('WORKFLOW_INSTRUCTIONS_REVIEW_PROCESS_MESSAGE')}</div>
            {savedInstructions && (
              <div>
                <div className="review-label">
                  <label>{translate('EOS_SETUP_FOLDER')}</label>
                </div>
                <div className="review-text spacer">{savedInstructions?.destinationFolderName}</div>
                <div className="review-label">
                  <label>{translate('EOS_SETUP_STUDY_GROUP')}</label>
                </div>
                <div className="review-text spacer">{savedInstructions?.studyGroupName}</div>
                <div className="review-label">
                  <label>{translate('EOS_SETUP_STUDY')}</label>
                </div>
                <div className="review-text spacer">{savedInstructions?.studyName}</div>
                <div className="review-label">
                  <label>{translate('EMTF_LEVEL')}</label>
                </div>
                <div className="review-text spacer">{STUDY_SITE}</div>
              </div>
            )}
            <div>
              <label>{translate('USER_S')}</label>
            </div>
          </div>

          <div className="eos-workflow-setup-table mb15">
            <div className="table-responsive">
              <table className="disabled styled-table mb0">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }} className="wrap-lines fs12">
                      {translate('EOS_SETUP_WF_ACCESS')}
                    </th>
                    <th style={{ width: '80px' }} className="wrap-lines fs12">
                      {translate('EOS_SETUP_WF_DOWNLOAD')}
                    </th>
                    <th style={{ width: '80px' }} className="wrap-lines fs12">
                      {translate('EOS_SETUP_WF_ACKNOWLEDGEMENT')}
                    </th>
                    <th className="fs12">
                      <span className="pl10">{translate('TABLE_TH_NAME')}</span>
                    </th>
                    <th style={{ width: '45%' }}>{translate('EMAIL')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <div className={`${row.isUser ? 'pl10 ' : ''}just-checkbox`}>
                          <input
                            type="checkbox"
                            id={row.rowId + '-access'}
                            checked={!!row.isAccessSelected}
                            disabled
                            readOnly
                          />
                          <label>
                            <div className="chkbc"></div>
                          </label>
                        </div>
                      </td>
                      <td>
                        <div className={`${row.isUser ? 'pl10 ' : ''}just-checkbox`}>
                          <input
                            type="checkbox"
                            id={row.rowId + '-download'}
                            checked={!!row.isDownloadSelected}
                            disabled
                            readOnly
                          />
                          <label>
                            <div className="chkbc"></div>
                          </label>
                        </div>
                      </td>
                      <td>
                        <div className={`${row.isUser ? 'pl10 ' : ''}just-checkbox`}>
                          <input
                            type="checkbox"
                            id={row.rowId + '-acknowledge'}
                            checked={!!row.isAcknowledgementSelected}
                            disabled
                            readOnly
                          />
                          <label>
                            <div className="chkbc"></div>
                          </label>
                        </div>
                      </td>
                      {row.isSite && (
                        <td colSpan={2}>
                          <span>{row.name}</span>
                        </td>
                      )}
                      {row.isUser && (
                        <td>
                          <div className="wrapped-name fs12">{row.name}</div>
                        </td>
                      )}
                      {row.isUser && <td className="email-control-wrapper fs12">{row.email ?? '-'}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination-container">
              <Paginate
                totalItems={metadata.current.totalUsersShown ?? 0}
                totalItemsFake={false}
                page={metadata.current.currentPage}
                pageChanged={changePage}
                itemsPerPage={metadata.current.pageSize}
                itemsPerPageChanged={changeItemsPerPage}
                txtTotalItems="PAGINATION_TOTAL_SITES"
                isEosWorkflowProcess={true}
              />
            </div>
          </div>

          <div className="country-level">
            <div className="review-label">
              <label>{translate('EOS_ACK_WF_INITIATOR')}</label>
            </div>
            <div className="review-text spacer">
              {adminDetails?.name} ({adminDetails?.email})
            </div>
          </div>

          <div className="fs12">
            <b>{translate('WORKFLOW_INSTRUCTIONS_REVIEW_MESSAGE')}</b>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Button variant="link" onClick={doClose}>
          {translate('CANCEL')}
        </Modal.Footer.Button>
        <div className="px-2">
          <Modal.Footer.Button variant="primary" onClick={revertInstructions}>
            {translate('REVERT_SELECTIONS')}
          </Modal.Footer.Button>
        </div>
        <Modal.Footer.Button variant="primary" onClick={startEos}>
          {translate('EOS_SETUP_USERS_WORKFLOWS_PROCESS_NOW_BTN')}
        </Modal.Footer.Button>
      </Modal.Footer>
    </Modal>
  )
}

export default EosWorkflowInstructionsReview
